import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Zap, Layers, Wand2, Download, Lock, Cpu } from "lucide-react";
import { Player } from "../animation/Player";
import { compileTimeline } from "@app/shared/templates";
import { AnimationIR } from "@app/shared/ir";
import { analyzeSvg, tagSvgPaths } from "@app/shared/analyze";
import { AI_BRANDS, TEMPLATE_BRAND, MOTION_BRAND } from "../animation/ai-icons";

const TEMPLATES_TO_SHOW = ["stroke_reveal", "scale_pop", "glitch_in", "elastic_intro", "cinematic_zoom", "liquid_reveal"] as const;

export function Landing() {
  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30 [mask-image:radial-gradient(70%_60%_at_50%_30%,#000,transparent)]" />
        <div className="mx-auto grid max-w-7xl gap-12 px-4 pb-16 pt-24 sm:px-6 lg:grid-cols-[1.1fr_1fr] lg:pt-32">
          <div className="relative z-10">
            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-xs text-white/70">
              <Sparkles className="h-3.5 w-3.5" /> Prompt-driven motion. No timelines, no rigs.
            </div>
            <h1 className="font-display text-5xl font-semibold leading-[1.05] tracking-tight text-white sm:text-6xl">
              Make your logo<br />
              <span className="bg-gradient-to-br from-white via-white/90 to-white/40 bg-clip-text text-transparent">come alive.</span>
            </h1>
            <p className="mt-5 max-w-xl text-base text-white/60 sm:text-lg">
              Upload an SVG, write what you want, get a cinematic animation ready to ship. Powered by Llama 3.3 on Cloudflare Workers AI, rendered with GSAP and exportable to WebM, MP4, and GIF.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link to="/signup" className="btn-primary px-5 py-2.5 text-sm">Start animating <ArrowRight className="h-4 w-4" /></Link>
              <Link to="/templates" className="btn-ghost px-5 py-2.5 text-sm">Browse 12 templates</Link>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-white/40">
              <span className="flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" /> Argon-class password hashing</span>
              <span className="flex items-center gap-1.5"><Zap className="h-3.5 w-3.5" /> Renders at the edge</span>
              <span className="flex items-center gap-1.5"><Download className="h-3.5 w-3.5" /> Free downloads, no watermark</span>
            </div>
          </div>
          <HeroDemo />
        </div>
      </section>

      {/* Trio of preview cards */}
      <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-6">
        <div className="mb-6 flex items-end justify-between">
          <div>
            <h2 className="text-2xl font-semibold tracking-tight text-white">A few of the moods</h2>
            <p className="text-sm text-white/50">Real AI brand logos, animated six different ways.</p>
          </div>
          <Link to="/templates" className="btn-quiet">See all <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {TEMPLATES_TO_SHOW.map((id, i) => (
            <TemplatePreviewCard key={id} templateId={id} index={i} />
          ))}
        </div>
      </section>

      {/* Supported AI models marquee */}
      <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-6">
        <div className="mb-6">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-xs text-white/70">
            <Cpu className="h-3.5 w-3.5" /> Multi-model
          </div>
          <h2 className="mt-4 text-2xl font-semibold tracking-tight text-white">Pick the brain behind your animation</h2>
          <p className="text-sm text-white/50">Default is Llama 3.3 on Cloudflare Workers AI. Switch to any of these in the editor — your IR plan is the same, only the model changes.</p>
        </div>
        <div className="card relative overflow-hidden p-6">
          <div className="grid grid-cols-4 gap-x-6 gap-y-5 sm:grid-cols-6 md:grid-cols-8">
            {AI_BRANDS.slice(0, 16).map((b) => (
              <div key={b.id} className="group flex flex-col items-center gap-2 text-white/60 transition-colors hover:text-white">
                <div className="grid h-12 w-12 place-items-center rounded-xl border border-white/[0.06] bg-white/[0.02] [&_svg]:h-7 [&_svg]:w-7" dangerouslySetInnerHTML={{ __html: b.svg }} />
                <span className="font-mono text-[10px]">{b.title}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6">
        <div className="mb-10">
          <h2 className="text-2xl font-semibold tracking-tight text-white">How it works</h2>
          <p className="text-sm text-white/50">Three steps. The LLM does the planning. You do the picking.</p>
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          <StepCard n={1} icon={<Layers className="h-5 w-5" />} title="Upload your SVG" body="We parse paths, colors, and geometry — never your account." />
          <StepCard n={2} icon={<Wand2 className="h-5 w-5" />} title="Describe the vibe" body="“luxury, slow, cinematic” → cinematic_zoom. “neon glitch” → glitch_in. Plain English." />
          <StepCard n={3} icon={<Download className="h-5 w-5" />} title="Render & export" body="WebM in your browser, MP4 from our Cloudflare Container fleet. Yours to keep." />
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-28 sm:px-6">
        <div className="card relative overflow-hidden p-10 sm:p-14">
          <div className="absolute inset-0 grid-bg opacity-20 [mask-image:radial-gradient(60%_80%_at_50%_50%,#000,transparent)]" />
          <div className="relative z-10 grid items-center gap-6 sm:grid-cols-[1.4fr_1fr]">
            <div>
              <h3 className="font-display text-3xl font-semibold tracking-tight text-white">Bring a logo. Take a movie.</h3>
              <p className="mt-2 max-w-lg text-sm text-white/60">No credit card. No watermarks on browser exports. Twelve templates, hand-tuned, ready in seconds.</p>
            </div>
            <div className="flex flex-wrap gap-3 sm:justify-end">
              <Link to="/signup" className="btn-primary px-5 py-2.5">Start free <ArrowRight className="h-4 w-4" /></Link>
              <Link to="/templates" className="btn-ghost px-5 py-2.5">See templates</Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function StepCard({ n, icon, title, body }: { n: number; icon: React.ReactNode; title: string; body: string }) {
  return (
    <div className="card card-hover p-6">
      <div className="mb-4 flex items-center justify-between">
        <span className="rounded-md bg-white/[0.04] p-2 text-white/80">{icon}</span>
        <span className="font-mono text-xs text-white/30">0{n}</span>
      </div>
      <h3 className="text-base font-semibold text-white">{title}</h3>
      <p className="mt-1 text-sm text-white/55">{body}</p>
    </div>
  );
}

function HeroDemo() {
  // Hero uses our own Motion mark — this is "our" product, not a brand showcase.
  const tagged = tagSvgPaths(MOTION_BRAND.svg);
  const analysis = analyzeSvg(tagged);
  const ir = AnimationIR.parse({
    templateId: "stroke_reveal",
    durationMs: 2200,
    easing: "easeInOutCubic",
    mood: "minimal",
    effects: [],
    colorOverrides: { transparent: true },
  });
  const timeline = compileTimeline(ir, analysis);
  return (
    <div className="relative z-10 flex items-center justify-center">
      <div className="card relative h-[420px] w-full max-w-md overflow-hidden">
        <div className="absolute inset-x-4 top-3 flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-white/20" />
            <span className="h-2 w-2 rounded-full bg-white/20" />
            <span className="h-2 w-2 rounded-full bg-white/20" />
          </div>
          <span className="font-mono text-[10px] text-white/30">stroke_reveal · 2.2s</span>
        </div>
        <div className="grid h-full place-items-center">
          <Player svg={tagged} timeline={timeline} loop autoplay />
        </div>
      </div>
    </div>
  );
}

function TemplatePreviewCard({ templateId, index }: { templateId: string; index: number }) {
  const brand = TEMPLATE_BRAND[templateId];
  const svg = tagSvgPaths(brand.svg);
  const analysis = analyzeSvg(svg);
  const ir = AnimationIR.parse({ templateId: templateId as any, durationMs: 1600, easing: "easeOutExpo", mood: "minimal", effects: [], colorOverrides: { transparent: true } });
  const timeline = compileTimeline(ir, analysis);
  return (
    <div className="card card-hover relative overflow-hidden">
      <div className="aspect-[4/3] w-full grid-bg relative">
        <span className="absolute left-3 top-3 z-10 rounded-md bg-black/30 px-2 py-0.5 font-mono text-[10px] text-white/60 backdrop-blur-sm">
          {brand.title}
        </span>
        <div className="grid h-full place-items-center">
          <Player svg={svg} timeline={timeline} loop autoplay delayMs={index * 240} />
        </div>
      </div>
      <div className="flex items-center justify-between border-t border-white/[0.06] px-4 py-3">
        <span className="text-sm font-medium text-white">{titleFor(templateId)}</span>
        <span className="font-mono text-[10px] text-white/40">{templateId}</span>
      </div>
    </div>
  );
}

function titleFor(id: string) {
  return id.split("_").map((s) => s[0]?.toUpperCase() + s.slice(1)).join(" ");
}
